import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle, Clock, Landmark, Truck, Lock, AlertCircle, DollarSign, Globe, MapPin, FileText, Package, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const SL = { pending_payment: "Pending Execution", payment_received: "Settlement Secured", processing: "Execution in Progress", shipped: "Shipment Dispatched", in_transit: "In Transit", delivered: "Delivered — Settlement Triggered", completed: "Settlement Released", disputed: "Settlement Held — Under Review" };
function fmtAmt(a, c) { return new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD", minimumFractionDigits: 0 }).format(a); }

export default function TransactionTrackPage() {
  const { token } = useParams();
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/t/${token}`).then(({ data }) => setTxn(data)).catch(() => setError("Transaction not found or link has expired.")).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-[#050A07] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#22C55E] border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#050A07] flex items-center justify-center px-6">
      <div className="text-center max-w-md"><Lock className="w-12 h-12 text-[#A1B2A8]/30 mx-auto mb-4" /><h1 className="font-['Outfit'] text-xl font-bold text-white mb-2">Access Denied</h1><p className="text-[#A1B2A8] text-sm">{error}</p></div>
    </div>
  );

  const isSupplier = txn.viewer_role === "supplier";
  const isDisputed = txn.status === "disputed";
  const capitalReleased = txn.status === "completed";
  const capitalSecured = ["payment_received", "processing", "shipped", "in_transit", "delivered", "completed"].includes(txn.status);
  const refId = `CF-${txn.id.slice(0, 8).toUpperCase()}`;
  const completedTriggers = (txn.trigger_conditions || []).filter(t => t.status === "completed").length;
  const totalTriggers = (txn.trigger_conditions || []).length;

  return (
    <div className="min-h-screen bg-[#050A07]" data-testid="transaction-track-page">
      <header className="border-b border-[#1A2E22] bg-[#050A07]/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLogo as="div" size="md" />
          <div className="flex items-center gap-3">
            <span className="text-[#A1B2A8]/40 text-xs font-mono">{refId}</span>
            <span className="text-[#A1B2A8] text-xs flex items-center gap-1"><Shield className="w-3 h-3" /> {isSupplier ? "Exporter" : "Buyer"} Trade View</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Status Hero */}
        <div className={`bg-[#09120D] border p-8 mb-6 relative overflow-hidden ${isDisputed ? "border-red-400/30" : "border-[#1A2E22]"}`} data-testid="track-header">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#22C55E]/[0.02] rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Landmark className={`w-5 h-5 ${isDisputed ? "text-red-400" : "text-[#22C55E]"}`} />
                <span className={`text-lg font-['Outfit'] font-semibold ${isDisputed ? "text-red-400" : "text-[#22C55E]"}`}>{SL[txn.status] || txn.status}</span>
              </div>
              <p className="text-white font-['Outfit'] font-semibold text-lg">{txn.description}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-[#A1B2A8]/40 text-xs font-mono">{refId}</span>
                {txn.capital_structure && <span className="text-[#A1B2A8]/40 text-xs border border-[#1A2E22] px-2 py-0.5">{txn.capital_structure}</span>}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[#A1B2A8] text-xs">Trade Value</p>
              <p className="font-['Outfit'] text-4xl font-bold text-white">{fmtAmt(txn.amount, txn.currency)}</p>
              <p className="text-[#A1B2A8]/30 text-xs mt-1">{txn.currency} &middot; {txn.capital_structure || txn.payment_method}</p>
            </div>
          </div>
        </div>

        {/* Supplier Capital Visibility */}
        {isSupplier && (
          <div className={`border p-6 mb-6 ${capitalReleased ? "bg-[#22C55E]/5 border-[#22C55E]/30" : capitalSecured ? "bg-blue-400/5 border-blue-400/20" : "bg-yellow-400/5 border-yellow-400/20"}`} data-testid="supplier-capital-visibility">
            <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Settlement Status for Exporter</h2>
            {capitalReleased ? (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0"><CheckCircle className="w-5 h-5 text-[#22C55E]" /></div>
                <div>
                  <p className="text-[#22C55E] font-['Outfit'] font-semibold text-lg">Settlement Released — {fmtAmt(txn.amount, txn.currency)}</p>
                  <p className="text-[#A1B2A8] text-sm mt-1">Funds have been released to your account. This transaction is complete.</p>
                  <p className="text-[#A1B2A8]/40 text-xs mt-2">Released on: {new Date(txn.updated_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                </div>
              </div>
            ) : capitalSecured ? (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-400/20 flex items-center justify-center flex-shrink-0"><Lock className="w-5 h-5 text-blue-400" /></div>
                <div>
                  <p className="text-blue-400 font-['Outfit'] font-semibold text-lg">Settlement Secured — {fmtAmt(txn.amount, txn.currency)} locked</p>
                  <p className="text-[#A1B2A8] text-sm mt-1">Buyer settlement is confirmed and held in controlled flow. Release will be triggered upon delivery confirmation by the buyer.</p>
                  <div className="mt-3 bg-[#050A07] border border-[#1A2E22] p-3 inline-block">
                    <p className="text-[#A1B2A8]/60 text-xs">Trigger Progress</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-24 h-1.5 bg-[#1A2E22] rounded-full overflow-hidden">
                        <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${totalTriggers ? (completedTriggers / totalTriggers) * 100 : 0}%` }} />
                      </div>
                      <span className="text-blue-400 text-xs font-mono">{completedTriggers}/{totalTriggers}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-yellow-400" /></div>
                <div>
                  <p className="text-yellow-400 font-['Outfit'] font-semibold text-lg">Settlement Pending — Awaiting Buyer Execution</p>
                  <p className="text-[#A1B2A8] text-sm mt-1">The buyer has been sent a secure execution link. Settlement will be secured once the buyer completes the transfer.</p>
                  <p className="text-[#A1B2A8]/40 text-xs mt-2">Expected amount: {fmtAmt(txn.amount, txn.currency)}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Buyer trust section (non-supplier view) */}
        {!isSupplier && !capitalReleased && (
          <div className="bg-[#09120D] border border-[#22C55E]/15 p-6 mb-6" data-testid="buyer-trust">
            <h2 className="font-['Outfit'] text-sm font-semibold text-[#22C55E] mb-3 uppercase tracking-wider">Trade Protection Status</h2>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#22C55E] mt-0.5 flex-shrink-0" />
              <p className="text-[#A1B2A8] text-sm">Your trade is protected through Delta GreenX's operating infrastructure. Funds are held and only released to the exporter upon confirmed delivery. All milestones are tracked and verified.</p>
            </div>
          </div>
        )}

        {/* Parties */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="bg-[#09120D] border border-[#1A2E22] p-5">
            <div className="flex items-center gap-2 mb-2"><MapPin className="w-3.5 h-3.5 text-[#22C55E]" /><p className="text-[#A1B2A8] text-xs uppercase tracking-wider">Buyer</p></div>
            <p className="text-white text-sm font-semibold">{txn.buyer_name}</p>
            <p className="text-[#A1B2A8]/50 text-xs mt-0.5">{txn.buyer_company}</p>
            <p className="text-[#A1B2A8]/30 text-xs mt-1">{txn.buyer_email}</p>
          </div>
          <div className="bg-[#09120D] border border-[#1A2E22] p-5">
            <div className="flex items-center gap-2 mb-2"><Globe className="w-3.5 h-3.5 text-[#22C55E]" /><p className="text-[#A1B2A8] text-xs uppercase tracking-wider">Supplier</p></div>
            <p className="text-white text-sm font-semibold">{txn.supplier_name}</p>
            <p className="text-[#A1B2A8]/50 text-xs mt-0.5">{txn.supplier_company}</p>
            <p className="text-[#A1B2A8]/30 text-xs mt-1">{txn.supplier_email}</p>
          </div>
        </div>

        {/* Financial Details */}
        <div className="bg-[#09120D] border border-[#1A2E22] p-6 mb-6" data-testid="financial-details">
          <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Financial Details</h2>
          <div className="space-y-3">
            {[
              ["Reference", refId],
              ["Capital Amount", fmtAmt(txn.amount, txn.currency)],
              ["Capital Structure", txn.capital_structure || txn.payment_method],
              ["Currency", txn.currency],
              ["Description", txn.description],
              ["Initiated", new Date(txn.created_at).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })],
              ["Last Updated", new Date(txn.updated_at).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-[#1A2E22] pb-3 last:border-0 last:pb-0">
                <span className="text-[#A1B2A8] text-sm">{k}</span>
                <span className="text-white text-sm text-right max-w-[60%]">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Settlement Release Triggers */}
        {txn.trigger_conditions && txn.trigger_conditions.length > 0 && (
          <div className="bg-[#09120D] border border-[#1A2E22] p-6 mb-6" data-testid="trigger-conditions">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Outfit'] text-sm font-semibold text-white uppercase tracking-wider">Settlement Release Triggers</h2>
              <span className="text-[#A1B2A8]/40 text-xs">{completedTriggers} of {totalTriggers} completed</span>
            </div>
            <div className="space-y-4">
              {txn.trigger_conditions.map((tc, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${tc.status === "completed" ? "bg-[#22C55E] border-[#22C55E]" : "bg-transparent border-[#1A2E22]"}`}>
                      {tc.status === "completed" ? <CheckCircle className="w-4 h-4 text-white" /> : <span className="text-[#A1B2A8]/30 text-xs font-mono">{i + 1}</span>}
                    </div>
                    {i < txn.trigger_conditions.length - 1 && <div className={`w-px h-8 ${tc.status === "completed" ? "bg-[#22C55E]/30" : "bg-[#1A2E22]"}`} />}
                  </div>
                  <div className="pt-0.5">
                    <p className={`text-sm font-medium ${tc.status === "completed" ? "text-white" : "text-[#A1B2A8]/40"}`}>{tc.milestone}</p>
                    <p className={`text-xs mt-0.5 ${tc.status === "completed" ? "text-[#A1B2A8]/60" : "text-[#A1B2A8]/20"}`}>{tc.action}</p>
                  </div>
                  {tc.status === "completed" && <CheckCircle className="w-4 h-4 text-[#22C55E]/40 ml-auto mt-1" />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {txn.timeline && txn.timeline.length > 0 && (
          <div className="bg-[#09120D] border border-[#1A2E22] p-6" data-testid="track-timeline">
            <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-5 uppercase tracking-wider">Execution Timeline</h2>
            <div className="space-y-0">
              {[...txn.timeline].reverse().map((e, i) => (
                <div key={i} className="flex gap-4 relative">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${i === 0 ? "bg-[#22C55E] ring-4 ring-[#22C55E]/10" : "bg-[#1A2E22]"}`} />
                    {i < txn.timeline.length - 1 && <div className="w-px h-full bg-[#1A2E22] min-h-[48px]" />}
                  </div>
                  <div className="pb-6">
                    <p className={`text-sm font-medium ${i === 0 ? "text-white" : "text-[#A1B2A8]"}`}>{e.event}</p>
                    <p className="text-[#A1B2A8]/60 text-xs mt-1">{e.description}</p>
                    <p className="text-[#A1B2A8]/25 text-xs mt-1.5 font-mono">{new Date(e.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-[#1A2E22] py-8 mt-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-[#22C55E]/40" />
              <p className="text-[#A1B2A8]/40 text-xs">256-bit encrypted &middot; SOC 2 compliant &middot; PCI DSS Level 1</p>
            </div>
            <p className="text-[#A1B2A8]/25 text-xs">Delta GreenX — Operating Infrastructure for Global Food Trade &middot; {refId}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
